import { createApiRef } from '@backstage/core-plugin-api';
import { AxiosInstance } from 'axios';
import { AxiosInstanceProviderApi } from './axiosInstanceApi';

export interface PermitBuildApi {
  getAllTickets: () => Promise<any>;
  saveTicket: (ticket: any) => Promise<any>;
  deleteTicket: (id: any) => Promise<any>;
  batchSaveAssets: (ticketNumber: any, assets: any) => Promise<any>;
  getTicketDetails: (ticketNumber: any) => Promise<any>;
  saveNewAssetRequest: (newAsset: any) => Promise<any>;
  sendReminder: (id: any) => Promise<any>;
  saveComment: (comment: any) => Promise<any>;
  triggerTicketRun: (ticketNumber: any) => Promise<any>;
  getTicketRunDetails: (ticketNumber: any) => Promise<any>;
  getDownloadableFiles: (jsonFilePath: string, filePath: string) => Promise<any>;
}

export const permitBuildApiRef = createApiRef<PermitBuildApi>({
  id: 'cbre.devx.api.permit.build',
});

export class PermitBuildApiImpl implements PermitBuildApi {
  private readonly axiosInstancePromise: Promise<AxiosInstance>;

  constructor(axiosInstanceProviderApi: AxiosInstanceProviderApi) {
    this.axiosInstancePromise = axiosInstanceProviderApi.getInstance();
  }

  async getAllTickets(): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/p2b/tickets`)
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error occurred while getting ticket info ${err.message}`,
        );
      });
  }

  async getTicketDetails(ticketNumber: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .get(`service/p2b/ticket/${ticketNumber}`)
      .then(res => res?.data)
      .catch(err => {
        throw new Error(
          `Error occurred while getting ticket details ${err.message}`,
        );
      });
  }

  async batchSaveAssets(ticketNumber: any, assets: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'put',
        url: `service/p2b/ticket/${ticketNumber}/assets`,
        data: [...assets],
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while saving assets ${err.message}`);
      });
  }

  async saveNewAssetRequest(newAsset: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: newAsset?.id ? 'put' : 'post',
        url: 'service/p2b/asset',
        data: { ...newAsset },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while saving new asset request ${err.message}`);
      });
  }

  async sendReminder(id: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/p2b/asset/${id}/notification`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while sending reminder ${err.message}`);
      });
  }

  async saveTicket(ticket: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: ticket?.id ? 'put' : 'post',
        url: 'service/p2b/ticket',
        data: { ...ticket },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while saving ticket ${err.message}`);
      });
  }

  async deleteTicket(id: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'delete',
        url: `service/p2b/ticket/${id}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while deleting ticket ${err.message}`);
      });
  }

  async saveComment(comment: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: comment?.comment_id ? 'put' : 'post',
        url: 'service/p2b/comment',
        data: { ...comment },
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while saving new comment ${err.message}`);
      });
  }

  async triggerTicketRun(ticketNumber: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'post',
        url: `service/p2b/run/ticket/${ticketNumber}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while triggering ticket run ${err.message}`);
      });
  }

  async getTicketRunDetails(ticketNumber: any): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `service/p2b/run/ticket/${ticketNumber}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting ticket run details ${err.message}`);
      });
  }

  async getDownloadableFiles(jsonFilePath: string, filePath: string): Promise<any> {
    const instance = await this.axiosInstancePromise;
    return instance
      .request({
        method: 'get',
        url: `p2b?jsonFilePath=${jsonFilePath}&filePath=${filePath}`,
      })
      .then(res => res?.data)
      .catch(err => {
        throw new Error(`Error while getting downloadable files ${err.message}`);
      });
  }
}
